import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { QuotaDecisionModal } from "./QuotaDecisionModal";
import { Alert, Button, Card, CardHeader, Input, Tag, buttonStyles } from "./ui";
import { useOrgQuota, useQuotaRequests, useSetOrgQuota } from "../lib/queries";
import type { QuotaRequest, QuotaResource } from "../lib/types";

/**
 * An organization's ceilings on its own page: what they are, what is
 * counted against them, whether each is the platform default or one an
 * operator set, and any request waiting -- decidable here without a trip
 * to the queue. A superadmin can also set a ceiling directly, or clear it
 * back to the default; both are audited as `quota.set`.
 */
const RESOURCES: { key: QuotaResource; label: string }[] = [
  { key: "apps", label: "Apps" },
  { key: "databases", label: "Databases" },
  { key: "tunnels", label: "Private connections" },
  { key: "domains", label: "Domain names" },
  { key: "custom_domains", label: "Custom hostnames" },
];

export function OrgLimitsCard({ orgId, canWrite }: { orgId: string; canWrite: boolean }) {
  const quota = useOrgQuota(orgId);
  const requests = useQuotaRequests("requested", 100, orgId);
  const setQuota = useSetOrgQuota(orgId);
  const [editing, setEditing] = useState<QuotaResource | null>(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<{ request: QuotaRequest; decision: "approved" | "denied" } | null>(null);

  const waiting = (resource: QuotaResource) =>
    requests.data?.data.find((r) => r.resource === resource) ?? null;

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!editing) return;
    setError(null);
    try {
      await setQuota.mutateAsync({ [editing]: value === "" ? null : Number(value) });
      setEditing(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The limit could not be saved.");
    }
  }

  return (
    <Card>
      <CardHeader
        title="Limits"
        description="What this organization can create. Blank means the platform default."
        action={
          <Link to={`/organizations/limit-requests?org=${orgId}`} className={buttonStyles("ghost", "sm")}>
            Request history
          </Link>
        }
      />
      {error && (
        <div className="px-5 pt-4">
          <Alert>{error}</Alert>
        </div>
      )}
      <ul className="divide-y divide-line">
        {RESOURCES.map(({ key, label }) => {
          const line = quota.data?.[key];
          const request = waiting(key);
          return (
            <li key={key} className="space-y-2 px-5 py-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-fg">{label}</p>
                  <p className="text-xs text-fg-subtle">
                    {line ? `${line.used} of ${line.limit}` : "…"}
                    {line?.custom ? " · set for this organization" : " · platform default"}
                  </p>
                </div>
                {canWrite && editing !== key && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing(key);
                      setValue(line?.custom ? String(line.limit) : "");
                      setError(null);
                    }}
                  >
                    Set
                  </Button>
                )}
              </div>

              {editing === key && (
                <form onSubmit={save} className="flex flex-wrap items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    value={value}
                    placeholder="default"
                    onChange={(e) => setValue(e.target.value)}
                    className="w-28"
                    aria-label={`${label} limit`}
                  />
                  <Button type="submit" size="sm" loading={setQuota.isPending}>
                    Save
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(null)}>
                    Cancel
                  </Button>
                </form>
              )}

              {request && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/40 bg-warn/5 px-3 py-2">
                  <Tag tone="warn">asked for {request.requestedLimit}</Tag>
                  <p className="min-w-0 flex-1 text-[13px] text-fg-muted">{request.reason}</p>
                  {canWrite && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => setDeciding({ request, decision: "denied" })}>
                        Deny
                      </Button>
                      <Button size="sm" onClick={() => setDeciding({ request, decision: "approved" })}>
                        Approve
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <QuotaDecisionModal
        request={deciding?.request ?? null}
        decision={deciding?.decision ?? "approved"}
        onClose={() => setDeciding(null)}
      />
    </Card>
  );
}
